import React, { useState, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import {
  Activity, Users, Sparkles, Send,
  Network, AlertTriangle,
  Download, BookOpen, Sliders, BarChart2, Globe, CheckCircle,
  FileText, Target, Clock, Shield
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, Tooltip as ReTooltip,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
} from 'recharts';

/* ─── Types ──────────────────────────────────────────────────────────────── */
type ModelKey = 'xgboost' | 'llm' | 'gnn' | 'hybrid';
type SimMode = 'single' | 'compare' | 'whatif';

interface PredictionResult {
  virality_score: number;
  expected_reach: number;
  risk_level: 'HIGH' | 'MEDIUM' | 'LOW';
  risk_score?: number;
  platform_breakdown?: { x: number; instagram: number; tiktok: number };
  red_flags?: string[];
  virality_breakdown?: string;
  peak_hours?: number;
  counter_narrative_score?: number;
  counter_narrative_text?: string;
  narrative_vectors?: { pressure: number; intent: number; virality: number; coordination: number; velocity: number };
}

interface SimResult {
  prediction: PredictionResult;
  geolocations?: { name: string }[];
}

/* ─── Static data ─────────────────────────────────────────────────────────── */
const DEMO_RESULT: SimResult = {
  prediction: {
    virality_score: 0.98,
    expected_reach: 231000,
    risk_level: 'HIGH',
    risk_score: 92,
    platform_breakdown: { x: 112, instagram: 68, tiktok: 51 },
    red_flags: [
      'Matches 3 known disinformation patterns',
      'Linked to 2 shell domains in Intelligence Center',
      'High narrative overlap with Vaccine Backlash cluster',
    ],
    virality_breakdown: '87% emotional trigger · 11% timing signal · 2% cross-platform match',
    peak_hours: 14,
    counter_narrative_score: 34,
    counter_narrative_text: 'Pre-bunk angle: Emphasise WHO data showing vaccine adverse events are 0.003% — use infographic format for max debunkability.',
    narrative_vectors: { pressure: 91, intent: 78, virality: 98, coordination: 65, velocity: 84 },
  },
  geolocations: [{ name: 'Delhi' }, { name: 'Mumbai' }, { name: 'London' }],
};

const DEMO_COMPARE: SimResult[] = [
  { prediction: { ...DEMO_RESULT.prediction, virality_score: 0.98, expected_reach: 231000, risk_score: 92 } },
  { prediction: { ...DEMO_RESULT.prediction, virality_score: 0.84, expected_reach: 158000, risk_score: 76 } },
  { prediction: { ...DEMO_RESULT.prediction, virality_score: 0.72, expected_reach: 94000, risk_score: 61 } },
];

const MODEL_OPTIONS: { key: ModelKey; label: string; desc: string }[] = [
  { key: 'xgboost', label: 'XGBoost', desc: 'Gradient boosted decision tree' },
  { key: 'llm', label: 'LLM Ensemble', desc: '7-agent language model jury' },
  { key: 'gnn', label: 'Graph Neural Net', desc: 'Social network propagation model' },
  { key: 'hybrid', label: 'Hybrid (All)', desc: 'Consensus across all models' },
];

const SUGGEST_CHIPS = [
  { label: 'Election claim', text: 'BREAKING: Voter machines in 5 swing states are pre-programmed. Anonymous official confirms.' },
  { label: 'Health scare', text: 'New study: mRNA vaccine causes micro-clots in 1 in 300 people. Major hospitals on alert.' },
  { label: 'Supply-chain rumor', text: 'Exclusive: Global fuel shortage incoming — governments buying 60-day reserves in secret.' },
];

const COMPARE_VARIANTS = [
  'BREAKING: Voter machines in 5 swing states pre-programmed.',
  'CONFIRMED: Election officials admit machines malfunctioned in 3 states.',
  'LEAKED DOC: DHS memo warns of voting system vulnerabilities.',
];

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function fmtK(n: number) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return Math.floor(n / 1_000) + 'k';
  return String(n);
}

function riskColor(level: string) {
  if (level === 'HIGH') return { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' };
  if (level === 'MEDIUM') return { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' };
  return { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' };
}

function buildSparklineData(score: number): { t: number; v: number }[] {
  const base = score * 100;
  return [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22].map((t, i) => ({
    t,
    v: Math.min(100, Math.max(0, base * (0.1 + (i / 14)) + (Math.sin(i) * 4))),
  }));
}

function buildRadarData(v: NonNullable<PredictionResult['narrative_vectors']>) {
  return [
    { axis: 'Pressure', val: v.pressure },
    { axis: 'Intent', val: v.intent },
    { axis: 'Virality', val: v.virality },
    { axis: 'Coordination', val: v.coordination },
    { axis: 'Velocity', val: v.velocity },
  ];
}

/* ─── Sub-components ──────────────────────────────────────────────────────── */
function Spark({ data }: { data: { t: number; v: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={40}>
      <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <Line type="monotone" dataKey="v" stroke="#818cf8" strokeWidth={1.5} dot={false} />
        <ReTooltip
          contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 6, fontSize: 10 }}
          formatter={(v: number) => [`${v.toFixed(0)}%`, 'Projected']}
          labelFormatter={(l: number) => `+${l}h`}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function NarrativeRadar({ vectors }: { vectors: NonNullable<PredictionResult['narrative_vectors']> }) {
  const data = buildRadarData(vectors);
  return (
    <ResponsiveContainer width="100%" height={180}>
      <RadarChart data={data}>
        <PolarGrid stroke="#3f3f46" />
        <PolarAngleAxis dataKey="axis" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
        <Radar name="Fingerprint" dataKey="val" stroke="#818cf8" fill="#818cf8" fillOpacity={0.25} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

/* ─── Platform bar ────────────────────────────────────────────────────────── */
function PlatformBar({ breakdown }: { breakdown: { x: number; instagram: number; tiktok: number } }) {
  const total = breakdown.x + breakdown.instagram + breakdown.tiktok;
  const pct = (n: number) => ((n / total) * 100).toFixed(0);
  return (
    <div className="mt-3">
      <div className="flex h-2 rounded-full overflow-hidden mb-1.5">
        <div className="bg-sky-400 transition-all" style={{ width: `${pct(breakdown.x)}%` }} />
        <div className="bg-pink-400 transition-all" style={{ width: `${pct(breakdown.instagram)}%` }} />
        <div className="bg-violet-400 transition-all" style={{ width: `${pct(breakdown.tiktok)}%` }} />
      </div>
      <div className="flex gap-3 text-xs text-zinc-400">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-sky-400 inline-block" /> X {fmtK(breakdown.x * 1000)}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-pink-400 inline-block" /> IG {fmtK(breakdown.instagram * 1000)}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-violet-400 inline-block" /> TT {fmtK(breakdown.tiktok * 1000)}</span>
      </div>
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────────────────────── */
export const SimulationSandbox: React.FC = () => {
  const [text, setText] = useState('Breaking: Lockdown announced today, stock up on food');
  const [model, setModel] = useState<ModelKey>('xgboost');
  const [simMode, setSimMode] = useState<SimMode>('single');
  const [influencerBoost, setInfluencerBoost] = useState(1.0);
  const [platformBias, setPlatformBias] = useState<'all' | 'x' | 'instagram' | 'tiktok'>('all');
  const [timingHour, setTimingHour] = useState(9);

  const [loading, setLoading] = useState(false);
  const [loadingPct, setLoadingPct] = useState(0);
  const [loadingPhase, setLoadingPhase] = useState('');
  const [result, setResult] = useState<SimResult | null>(null);
  const [compareResults, setCompareResults] = useState<SimResult[] | null>(null);
  const [isLive, setIsLive] = useState<boolean | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const PHASES = [
    'Parsing narrative structure…',
    'Running XGBoost trees…',
    'Scoring emotional trigger vectors…',
    'Cross-referencing OSINT cluster signals…',
    'Computing platform propagation curves…',
    'Generating counter-narrative score…',
  ];

  const animateProgress = useCallback((phases: string[], duration: number, onDone: () => void) => {
    let step = 0;
    const totalSteps = 60;
    const interval = duration / totalSteps;
    setLoadingPct(0);
    setLoadingPhase(phases[0]);
    timerRef.current = setInterval(() => {
      step++;
      setLoadingPct(Math.round((step / totalSteps) * 100));
      setLoadingPhase(phases[Math.min(Math.floor((step / totalSteps) * phases.length), phases.length - 1)]);
      if (step >= totalSteps) {
        clearInterval(timerRef.current!);
        onDone();
      }
    }, interval);
  }, []);

  const runSimulate = async () => {
    setLoading(true);
    setResult(null);
    setCompareResults(null);
    animateProgress(PHASES, 2000, () => setLoading(false));
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/simulation/predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, model, mode: simMode, influencerBoost, platformBias, timingHour }),
      });
      const data = await res.json();
      if (simMode === 'compare') {
        setCompareResults([data, ...DEMO_COMPARE.slice(1)]);
      } else {
        setResult(data);
      }
      setIsLive(true);
    } catch {
      if (simMode === 'compare') {
        setCompareResults(DEMO_COMPARE);
      } else {
        setResult(DEMO_RESULT);
      }
      setIsLive(false);
    }
  };

  const handleExportJSON = () => {
    const payload = simMode === 'compare' ? compareResults : result;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `virality-simulation-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeResult = simMode === 'compare' ? compareResults?.[0]?.prediction : result?.prediction;
  const hasResult = simMode === 'compare' ? !!compareResults : !!result;
  const sparkData = activeResult ? buildSparklineData(activeResult.virality_score) : [];
  const rc = activeResult ? riskColor(activeResult.risk_level) : { text: '', bg: '', border: '' };

  return (
    <div className="bg-zinc-900/40 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6 flex flex-col shadow-2xl relative overflow-hidden group">
      {/* Ambient glow */}
      <div className="absolute -right-10 -top-10 w-48 h-48 bg-indigo-500/8 rounded-full blur-3xl group-hover:bg-indigo-500/15 transition-all duration-700 pointer-events-none" />
      <div className="absolute -left-10 bottom-10 w-32 h-32 bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 mb-2 relative z-10">
        <div className="p-2 bg-indigo-500/20 rounded-lg flex-shrink-0 mt-0.5">
          <Sparkles className="w-5 h-5 text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-bold text-white tracking-tight">XGBoost Virality Simulation</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${
              isLive === null ? 'bg-zinc-700/40 border border-zinc-600/40 text-zinc-400'
              : isLive ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
              : 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isLive === null ? 'bg-zinc-500' : isLive ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
              {isLive === null ? 'Ready' : isLive ? 'Live ML Engine' : 'Demo Mode'}
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">Multi-Model Narrative Simulator — test hypothetical text against live OSINT + 7-agent ML ensemble</p>
        </div>
      </div>

      {/* ── Model Selector ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-1.5 mb-4 relative z-10">
        {MODEL_OPTIONS.map(m => (
          <button
            key={m.key}
            onClick={() => setModel(m.key)}
            title={m.desc}
            className={`text-[11px] px-2.5 py-1 rounded-lg font-semibold border transition-all ${
              model === m.key
                ? 'bg-indigo-600/80 border-indigo-500 text-white'
                : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-indigo-500/40 hover:text-zinc-200'
            }`}
          >
            {m.key === 'xgboost' && <span className="mr-1">🌲</span>}
            {m.key === 'llm' && <span className="mr-1">🧠</span>}
            {m.key === 'gnn' && <span className="mr-1">🕸</span>}
            {m.key === 'hybrid' && <span className="mr-1">⚡</span>}
            {m.label}
          </button>
        ))}
      </div>

      {/* ── Input area ───────────────────────────────────────────────────── */}
      <div className="relative mb-3 z-10">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={4}
          className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl p-4 pr-24 text-white text-sm focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all placeholder-zinc-600 resize-none font-medium leading-relaxed shadow-inner"
          placeholder="Paste a post, thread, headline, or hypothetical claim…"
        />
        <div className="absolute right-3 top-3 flex flex-col gap-1">
          <span className="text-xs font-bold text-indigo-400/70 tracking-widest text-right">OSINT ENGINE V2</span>
        </div>
        <div className="absolute right-3 bottom-3 text-xs text-zinc-400 font-mono">
          {text.length} chars
        </div>
      </div>

      {/* Quick-add chips */}
      <div className="flex flex-wrap gap-1.5 mb-4 z-10 relative">
        {SUGGEST_CHIPS.map(chip => (
          <button
            key={chip.label}
            onClick={() => setText(chip.text)}
            className="text-xs px-2 py-1 rounded-md bg-zinc-800/60 border border-zinc-700/50 text-zinc-400 hover:text-indigo-300 hover:border-indigo-500/40 transition-all font-medium"
          >
            Try: {chip.label}
          </button>
        ))}
        <button className="text-xs px-2 py-1 rounded-md bg-zinc-800/60 border border-zinc-700/50 text-zinc-400 hover:text-indigo-300 hover:border-indigo-500/40 transition-all font-medium flex items-center gap-1">
          <BookOpen className="w-3 h-3" /> Load from Intel Center
        </button>
      </div>

      {/* ── Sim mode + Simulate button ────────────────────────────────────── */}
      <div className="flex gap-2 mb-3 z-10 relative">
        {(['single', 'compare', 'whatif'] as SimMode[]).map(mode => (
          <button
            key={mode}
            onClick={() => setSimMode(mode)}
            className={`flex-1 text-[11px] py-1.5 rounded-lg border font-semibold transition-all ${
              simMode === mode
                ? 'bg-violet-600/40 border-violet-500/60 text-violet-200'
                : 'bg-zinc-800/40 border-zinc-700/40 text-zinc-400 hover:text-zinc-300'
            }`}
          >
            {mode === 'single' && 'Single Run'}
            {mode === 'compare' && 'Compare 3 Variants'}
            {mode === 'whatif' && 'What-If Builder'}
          </button>
        ))}
      </div>

      {/* What-If sliders */}
      {simMode === 'whatif' && (
        <div className="mb-4 p-3 rounded-xl bg-zinc-800/30 border border-zinc-700/30 z-10 relative space-y-3">
          <div className="text-[11px] text-zinc-400 font-bold uppercase tracking-wider flex items-center gap-1.5 mb-1">
            <Sliders className="w-3.5 h-3.5 text-violet-400" /> Reach Modifiers
          </div>
          <div>
            <div className="flex justify-between text-xs text-zinc-400 mb-1">
              <span>Influencer Boost</span><span className="font-mono text-violet-300">{influencerBoost.toFixed(1)}x</span>
            </div>
            <input type="range" min={0.5} max={5} step={0.1} value={influencerBoost}
              onChange={e => setInfluencerBoost(parseFloat(e.target.value))}
              className="w-full accent-violet-500 h-1.5" />
          </div>
          <div>
            <div className="flex justify-between text-xs text-zinc-400 mb-1">
              <span>Timing (post hour)</span><span className="font-mono text-violet-300">{String(timingHour).padStart(2, '0')}:00</span>
            </div>
            <input type="range" min={0} max={23} step={1} value={timingHour}
              onChange={e => setTimingHour(parseInt(e.target.value))}
              className="w-full accent-violet-500 h-1.5" />
          </div>
          <div>
            <div className="text-xs text-zinc-400 mb-1.5">Platform Focus</div>
            <div className="flex gap-1.5">
              {(['all', 'x', 'instagram', 'tiktok'] as const).map(p => (
                <button key={p} onClick={() => setPlatformBias(p)}
                  className={`text-xs px-2 py-0.5 rounded border font-semibold transition-all ${
                    platformBias === p ? 'bg-sky-600/40 border-sky-500/60 text-sky-200' : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                  }`}>
                  {p === 'all' ? 'All' : p === 'x' ? 'X' : p === 'instagram' ? 'IG' : 'TikTok'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Big simulate button */}
      <button
        onClick={runSimulate}
        disabled={loading || !text.trim()}
        className="w-full bg-indigo-600 hover:bg-indigo-500 active:scale-[0.98] disabled:opacity-50 disabled:scale-100 text-white font-bold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/20 group/btn relative z-10 mb-2"
      >
        {loading ? (
          <div className="flex items-center gap-2 w-full flex-col">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              <span className="text-sm">{loadingPhase}</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-1 overflow-hidden">
              <div className="bg-white h-full rounded-full transition-all duration-150" style={{ width: `${loadingPct}%` }} />
            </div>
          </div>
        ) : (
          <>
            <Send className="w-4 h-4 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
            <span>
              {simMode === 'single' && 'Simulate Virality'}
              {simMode === 'compare' && 'Simulate & Compare 3 Variants'}
              {simMode === 'whatif' && 'Run What-If Scenario'}
            </span>
            {model === 'hybrid' && <span className="text-xs text-indigo-200 ml-1">(4 models)</span>}
          </>
        )}
      </button>

      {/* ── Results ──────────────────────────────────────────────────────── */}
      {hasResult && !loading && (
        <div className="mt-6 pt-5 border-t border-zinc-800/50 relative z-10 space-y-5">

          {/* Live / Demo badge */}
          <div className="flex items-center justify-between">
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${
              isLive ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
              : 'bg-amber-500/15 border border-amber-500/30 text-amber-400'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isLive ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
              {isLive ? 'Live Prediction' : 'Demo Fallback — API unavailable'}
            </span>
            <button onClick={handleExportJSON} className="text-xs text-zinc-400 hover:text-indigo-300 flex items-center gap-1 transition-colors">
              <Download className="w-3 h-3" /> Export JSON
            </button>
          </div>

          {/* ── Compare mode ── */}
          {simMode === 'compare' && compareResults && (
            <div className="space-y-2">
              <div className="text-[11px] text-zinc-400 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <BarChart2 className="w-3.5 h-3.5 text-indigo-400" /> Variant Comparison
              </div>
              {COMPARE_VARIANTS.map((v, i) => {
                const cr = compareResults[i]?.prediction;
                if (!cr) return null;
                const crRc = riskColor(cr.risk_level);
                return (
                  <div key={i} className="p-3 rounded-xl bg-zinc-800/30 border border-zinc-700/30">
                    <div className="text-xs text-zinc-400 mb-2 line-clamp-1 italic">"{v}"</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <div className="text-xs text-zinc-400 uppercase font-bold tracking-wider">Virality</div>
                        <div className="text-sm font-bold text-indigo-300 font-mono">{(cr.virality_score * 100).toFixed(0)}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-zinc-400 uppercase font-bold tracking-wider">Reach</div>
                        <div className="text-sm font-bold text-emerald-300 font-mono">{fmtK(cr.expected_reach)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-zinc-400 uppercase font-bold tracking-wider">Risk</div>
                        <div className={`text-sm font-bold font-mono ${crRc.text}`}>{cr.risk_level}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Single / what-if result cards ── */}
          {simMode !== 'compare' && activeResult && (
            <>
              {/* Three primary cards */}
              <div className="grid grid-cols-3 gap-3">
                {/* VIRALITY */}
                <div className="bg-zinc-800/30 rounded-xl p-3 border border-indigo-500/20 hover:border-indigo-500/40 transition-colors">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Activity className="w-3.5 h-3.5 text-indigo-400" />
                    <div className="text-xs text-zinc-400 uppercase font-bold tracking-wider">Virality</div>
                  </div>
                  <div className="font-bold text-xl text-white font-mono mb-1">
                    {(activeResult.virality_score * 100).toFixed(0)}%
                  </div>
                  <Spark data={sparkData} />
                  {activeResult.virality_breakdown && (
                    <div className="mt-1.5 text-xs text-zinc-400 leading-relaxed">{activeResult.virality_breakdown}</div>
                  )}
                </div>

                {/* REACH */}
                <div className="bg-zinc-800/30 rounded-xl p-3 border border-emerald-500/20 hover:border-emerald-500/40 transition-colors">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Users className="w-3.5 h-3.5 text-emerald-400" />
                    <div className="text-xs text-zinc-400 uppercase font-bold tracking-wider">Reach</div>
                  </div>
                  <div className="font-bold text-xl text-white font-mono mb-0.5">
                    {fmtK(activeResult.expected_reach)}
                  </div>
                  {activeResult.peak_hours && (
                    <div className="text-xs text-emerald-400/80 flex items-center gap-1 mb-1">
                      <Clock className="w-2.5 h-2.5" /> Peak in {activeResult.peak_hours}h
                    </div>
                  )}
                  {activeResult.platform_breakdown && (
                    <PlatformBar breakdown={activeResult.platform_breakdown} />
                  )}
                </div>

                {/* RISK */}
                <div className={`rounded-xl p-3 border transition-colors ${rc.bg} ${rc.border}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Shield className={`w-3.5 h-3.5 ${rc.text}`} />
                    <div className="text-xs text-zinc-400 uppercase font-bold tracking-wider">Risk</div>
                  </div>
                  <div className={`font-bold text-xl font-mono mb-0.5 ${rc.text}`}>
                    {activeResult.risk_level}
                  </div>
                  {activeResult.risk_score !== undefined && (
                    <div className={`text-xs font-mono font-bold ${rc.text}`}>{activeResult.risk_score}/100</div>
                  )}
                  {activeResult.red_flags && activeResult.red_flags.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {activeResult.red_flags.map((flag, i) => (
                        <div key={i} className="text-xs text-rose-300/80 flex items-start gap-1">
                          <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0 mt-0.5 text-rose-400" />
                          <span>{flag}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Narrative Fingerprint + Counter-Narrative */}
              <div className="grid grid-cols-2 gap-4">
                {/* Radar */}
                {activeResult.narrative_vectors && (
                  <div className="bg-zinc-800/20 rounded-xl p-3 border border-zinc-700/30">
                    <div className="text-xs text-zinc-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <Network className="w-3 h-3 text-indigo-400" /> Narrative Fingerprint
                    </div>
                    <NarrativeRadar vectors={activeResult.narrative_vectors} />
                  </div>
                )}

                {/* Counter-narrative */}
                {activeResult.counter_narrative_score !== undefined && (
                  <div className="bg-zinc-800/20 rounded-xl p-3 border border-zinc-700/30 flex flex-col">
                    <div className="text-xs text-zinc-400 font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <CheckCircle className="w-3 h-3 text-emerald-400" /> Counter-Narrative Score
                    </div>
                    <div className="flex items-end gap-2 mb-2">
                      <span className="text-2xl font-bold font-mono text-emerald-400">{activeResult.counter_narrative_score}</span>
                      <span className="text-xs text-zinc-400 mb-0.5">/100 debunkability</span>
                    </div>
                    <div className="w-full bg-zinc-700/40 rounded-full h-1.5 mb-3">
                      <div className="bg-emerald-500 h-full rounded-full transition-all" style={{ width: `${activeResult.counter_narrative_score}%` }} />
                    </div>
                    {activeResult.counter_narrative_text && (
                      <p className="text-xs text-zinc-400 leading-relaxed italic flex-1">
                        {activeResult.counter_narrative_text}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Geolocations */}
              {result?.geolocations && result.geolocations.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-zinc-400 bg-indigo-500/5 border border-indigo-500/20 p-3 rounded-xl">
                  <Globe className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                  <span className="font-bold text-indigo-300">Geo signals:</span>
                  <span className="text-zinc-300 italic">{result.geolocations.map((g: { name: string }) => g.name).join(', ')}</span>
                </div>
              )}

              {/* Export buttons */}
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={handleExportJSON} className="text-[11px] px-3 py-1.5 rounded-lg bg-zinc-800/60 border border-zinc-700/50 text-zinc-300 hover:border-indigo-500/40 hover:text-indigo-300 transition-all flex items-center gap-1.5 font-semibold">
                  <FileText className="w-3 h-3" /> Client Briefing PDF
                </button>
                <button className="text-[11px] px-3 py-1.5 rounded-lg bg-zinc-800/60 border border-zinc-700/50 text-zinc-300 hover:border-violet-500/40 hover:text-violet-300 transition-all flex items-center gap-1.5 font-semibold">
                  <Network className="w-3 h-3" /> Add to Narrative Map
                </button>
                <button className="text-[11px] px-3 py-1.5 rounded-lg bg-zinc-800/60 border border-zinc-700/50 text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-300 transition-all flex items-center gap-1.5 font-semibold">
                  <Target className="w-3 h-3" /> Save to Crisis Workspace
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SimulationSandbox;
